import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Globe, Search, RefreshCw, Mail, Server, ShieldCheck, ShieldAlert, Link as LinkIcon, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { YamlSourceBadge } from '@/components/YamlSourceBadge';

export default function DomainsPage() {
  const { authAxios } = useAuth();
  const [domains, setDomains] = useState([]);
  const [intel, setIntel] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scanning, setScanning] = useState(false);

  // Assignment Dialog State
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState('');

  const fetchData = async () => {
    try {
      const api = authAxios();
      const [domRes, intelRes, srvRes] = await Promise.all([
        api.get('/domains'),
        api.get('/domain-intel/latest'),
        api.get('/servers')
      ]);
      setDomains(Array.isArray(domRes.data) ? domRes.data : []);
      setIntel(Array.isArray(intelRes.data) ? intelRes.data : []);
      setServers(Array.isArray(srvRes.data) ? srvRes.data : []);
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleScan = async () => {
    setScanning(true);
    try {
      const api = authAxios();
      await api.post('/domain-intel/run');
      toast.success('Scan triggered successfully');
      setTimeout(fetchData, 2000);
    } catch (err) {
      toast.error('Scan failed to start');
    } finally {
      setScanning(false);
    }
  };

  const handleCreateSSL = async (domain, assetId) => {
    if (!assetId) {
      toast.error('Cannot create SSL: Domain not mapped to any server.');
      return;
    }
    
    if (!window.confirm(`Create SSL for ${domain} on server ${assetId}?\n\nThis will stop Nginx briefly to run Certbot.`)) {
      return;
    }

    const toastId = toast.loading(`Starting SSL creation for ${domain}...`);
    try {
      const api = authAxios();
      const res = await api.post('/playbooks/run', {
        playbook_id: 'certbot-force-standalone',
        asset_id: assetId,
        params: { domain }
      });
      
      if (res.data.status === 'success') {
        toast.success(`SSL Certificate created for ${domain}`, { id: toastId });
        // Refresh data after a short delay
        setTimeout(fetchData, 2000);
      } else {
        console.error(res.data);
        toast.error(`Failed: ${res.data.error || res.data.detail || res.data.stderr?.slice(-160) || 'Check console'}`, { id: toastId });
      }
    } catch (err) {
      console.error(err);
      toast.error(`Error: ${err.response?.data?.detail || err.response?.data?.error || err.message}`, { id: toastId });
    }
  };

  const openAssignDialog = (domain, currentAssetId) => {
    setSelectedDomain(domain);
    setSelectedAssetId(currentAssetId || '');
    setAssignDialogOpen(true);
  };

  const handleAssignSave = async () => {
    try {
      const api = authAxios();
      await api.patch(`/domains/${selectedDomain.domain}`, {
        assigned_asset_id: selectedAssetId === 'none' ? '' : selectedAssetId
      });
      toast.success(`Assigned ${selectedDomain.domain} to ${selectedAssetId === 'none' ? 'None' : selectedAssetId}`);
      setAssignDialogOpen(false);
      fetchData(); // Refresh to show update
    } catch (err) {
      console.error(err);
      toast.error('Failed to update assignment');
    }
  };

  const getMailStatusBadge = (mail) => {
    if (!mail) return <Badge className="bg-gray-500/10 text-gray-500">Unchecked</Badge>;
    
    // Check our summarized status first if available
    if (mail.mail_status) {
      switch (mail.mail_status) {
        case 'ok': return <Badge className="bg-emerald-500/10 text-emerald-500">Active</Badge>;
        case 'no_mx': return <Badge className="bg-gray-500/10 text-gray-500">No Mail</Badge>;
        case 'failed': return <Badge className="bg-red-500/10 text-red-500">Error</Badge>;
        default: break; // fallthrough
      }
    }

    // Fallback logic
    if (mail.mx_records?.length > 0) {
      return <Badge className="bg-emerald-500/10 text-emerald-500">MX Set</Badge>;
    }
    return <Badge className="bg-gray-500/10 text-gray-500">No MX</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ok': return <Badge className="bg-emerald-500/10 text-emerald-500">OK</Badge>;
      case 'external': return <Badge className="bg-blue-500/10 text-blue-500">External</Badge>;
      case 'dead': return <Badge className="bg-red-500/10 text-red-500">Dead</Badge>;
      case 'miswired': return <Badge className="bg-amber-500/10 text-amber-500">Miswired</Badge>;
      case 'parked': return <Badge className="bg-purple-500/10 text-purple-500">Parked</Badge>;
      default: return <Badge className="bg-gray-500/10 text-gray-500">Unknown</Badge>;
    }
  };

  const isExternalDomain = (dom) => dom?.intel?.status === 'external';
  const canRunInternalSsl = (dom) => {
    const status = dom?.intel?.status;
    if (!dom?.intel?.classification?.mapped_asset_id) return false;
    if (status === 'external' || status === 'parked') return false;
    return true;
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(domains)) return [];
    const safeIntel = Array.isArray(intel) ? intel : [];
    
    return domains.map(d => {
      // Find intel for this domain
      let info = safeIntel.find(i => i.fqdn === d.domain);
      
      // Clone or create fresh object to avoid mutating original state in cache
      if (info) {
        info = JSON.parse(JSON.stringify(info));
      } else {
        info = { status: 'unknown', classification: {}, tls: {} };
      }

      // Handle Manual Assignment
      if (d.assigned_asset_id) {
        info.classification = info.classification || {};
        info.classification.mapped_asset_id = d.assigned_asset_id;
        info.is_manual = true;
      }

      return { ...d, intel: info };
    }).filter(d => {
      const q = search.toLowerCase();
      return !q ||
        (d.domain || '').toLowerCase().includes(q) ||
        (d.owner || '').toLowerCase().includes(q);
    });
  }, [domains, intel, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
              Domains
            </h1>
            <YamlSourceBadge className="text-xs" />
          </div>
          <p className="text-sm text-[#a1a1aa] mt-1">
            {domains.length} domains managed
          </p>
        </div>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="h-10 px-4 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg flex items-center gap-2 text-sm transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning...' : 'Scan Now'}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
          <Input
            placeholder="Search domains..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-11 pl-10 bg-[#121214] border-white/10 focus:border-[#EF694C] text-white rounded-lg"
          />
        </div>
      </div>

      <div className="text-xs text-[#71717a]">
        `External` bedeutet: Ziel antwortet, liegt aber bewusst außerhalb des intern gemappten Server-Bestands und ist kein interner Certbot-Kandidat.
      </div>

      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#a1a1aa]">Domain</TableHead>
                <TableHead className="text-[#a1a1aa]">Intel Status</TableHead>
                <TableHead className="text-[#a1a1aa]">Owner</TableHead>
                <TableHead className="text-[#a1a1aa]">Asset Map</TableHead>
                <TableHead className="text-[#a1a1aa]">TLS Expiry</TableHead>
                <TableHead className="text-[#a1a1aa]">Mail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((dom) => (
                <TableRow key={dom.domain} className="border-white/5 hover:bg-white/[0.03]">
                  <TableCell className="font-medium text-white">
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-[#EF694C]" />
                      <div>
                        <div>{dom.domain}</div>
                        {dom.registrar && <div className="text-xs text-[#52525b]">Reg: {dom.registrar}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {dom.intel ? getStatusBadge(dom.intel.status) : <Badge className="bg-gray-500/10 text-gray-500">Unchecked</Badge>}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-[#a1a1aa] capitalize">{dom.owner}</span>
                  </TableCell>
                  <TableCell>
                    {dom.intel?.classification?.mapped_asset_id ? (
                      <div className="flex items-center gap-2 text-xs">
                        <Server className={`w-3 h-3 ${dom.intel.is_manual ? 'text-blue-400' : 'text-[#a1a1aa]'}`} />
                        <span className={dom.intel.is_manual ? 'text-blue-400 font-medium' : 'text-[#a1a1aa]'}>
                          {dom.intel.classification.mapped_asset_id}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-5 w-5 ml-1 text-[#52525b] hover:text-white"
                          onClick={() => openAssignDialog(dom, dom.intel.classification.mapped_asset_id)}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : isExternalDomain(dom) ? (
                      <div className="flex items-center gap-2 text-xs text-blue-400">
                        <Globe className="w-3 h-3" />
                        <span>{dom.intel?.classification?.provider || 'Extern verwaltet'}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                         <span className="text-xs text-[#52525b]">-</span>
                         <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-5 w-5 ml-1 text-[#52525b] hover:text-white"
                          onClick={() => openAssignDialog(dom, '')}
                        >
                          <LinkIcon className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {dom.intel?.tls?.days_remaining !== undefined ? (
                      canRunInternalSsl(dom) ? (
                      <div 
                        className={`flex items-center gap-1 text-xs cursor-pointer hover:underline ${
                          dom.intel.tls.days_remaining < 30 ? 'text-red-500' : 'text-emerald-500'
                        }`}
                        onClick={() => handleCreateSSL(dom.domain, dom.intel?.classification?.mapped_asset_id)}
                        title="Click to renew/create SSL"
                      >
                        {dom.intel.tls.days_remaining < 30 ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                        <span>{dom.intel.tls.days_remaining} days</span>
                      </div>
                      ) : (
                      <div
                        className={`flex items-center gap-1 text-xs ${
                          isExternalDomain(dom) ? 'text-blue-400' : 'text-[#52525b]'
                        }`}
                        title={isExternalDomain(dom) ? 'Extern verwaltetes Zertifikat' : 'Keine interne SSL-Aktion verfugbar'}
                      >
                        {dom.intel.tls.days_remaining < 30 ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                        <span>
                          {dom.intel.tls.days_remaining} days{isExternalDomain(dom) ? ' (extern)' : ''}
                        </span>
                      </div>
                      )
                    ) : (
                      canRunInternalSsl(dom) ? (
                      <span 
                        className="text-xs text-[#52525b] cursor-pointer hover:text-white"
                        onClick={() => handleCreateSSL(dom.domain, dom.intel?.classification?.mapped_asset_id)}
                        title="Click to create SSL"
                      >
                        -
                      </span>
                      ) : (
                      <span
                        className={`text-xs ${isExternalDomain(dom) ? 'text-blue-400' : 'text-[#52525b]'}`}
                        title={isExternalDomain(dom) ? 'Extern verwaltetes Ziel' : 'Keine interne SSL-Aktion verfugbar'}
                      >
                        {isExternalDomain(dom) ? 'Extern verwaltet' : '-'}
                      </span>
                      )
                    )}
                  </TableCell>
                  <TableCell>
                    {dom.mail ? (
                      <div className="flex flex-col gap-1 text-xs">
                        <div className="mb-1">{getMailStatusBadge(dom.mail)}</div>
                        {dom.mail.provider_asset_hint ? (
                           <div className="flex items-center gap-1 text-[#a1a1aa]">
                             <Mail className="w-3 h-3" />
                             <span>{dom.mail.provider_asset_hint}</span>
                           </div>
                        ) : dom.mail.mx_records && dom.mail.mx_records.length > 0 ? (
                           <div className="flex items-center gap-1 text-[#52525b]" title={dom.mail.mx_records[0]}>
                             <Mail className="w-3 h-3" />
                             <span className="truncate max-w-[120px]">
                               {dom.mail.mx_records[0].split(' ')[1] || 'MX'}
                             </span>
                           </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-xs text-[#52525b]">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Assign Server to {selectedDomain?.domain}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Target Server</Label>
              <Select value={selectedAssetId} onValueChange={setSelectedAssetId}>
                <SelectTrigger className="bg-[#121214] border-white/10 text-white">
                  <SelectValue placeholder="Select a server..." />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10 text-white">
                  <SelectItem value="none">None (Unassign)</SelectItem>
                  {servers.map(srv => (
                    <SelectItem key={srv.asset_id} value={srv.asset_id}>
                      {srv.asset_id} ({srv.host})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-yellow-500/80 bg-yellow-500/10 p-3 rounded border border-yellow-500/20">
              Note: This only updates the inventory mapping. You must manually update DNS records at your registrar to point to the server IP.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignDialogOpen(false)} className="text-[#a1a1aa] hover:text-white">Cancel</Button>
            <Button onClick={handleAssignSave} className="bg-[#EF694C] hover:bg-[#d65d43] text-white">Save Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
